import type { AthenaPrincipal } from "../../../runtime/data/principal.ts";
import { AthenaBillingCapabilityError } from "../../errors.ts";
import type { BillingProviderConfigMap } from "../../providers/types.ts";
import { prepareBillingCommand } from "../../safety/prepare.ts";
import type { BillingSqlExecutor } from "../../subject/repository.ts";
import type { BillingCheckout, BillingPrice } from "../../types.ts";
import { executeLocalBillingPaymentCreate } from "../local/execute/payments.ts";
import { executeLocalBillingPriceList } from "../local/execute/price-list.ts";
import { billingHalLinkHref } from "../hal-link-href.ts";
import type { BillingProviderRegistry } from "../local/providers/registry.ts";
import { PROCESS_BILLING_INVOCATION } from "../invocation-authority.ts";
import { resolveBillingCheckoutIntent } from "./checkout-intent.ts";
import { selfDelegatedBillingPrincipal } from "./delegated-principal.ts";
import { resolveAllowedBillingRedirectUrl } from "./redirect-url.ts";

const OWNED_SUBSCRIPTION_CUSTOMER_SQL = `
SELECT provider_customer_id
FROM billing.billing_subscriptions
WHERE subject_kind = 'user'
  AND subject_id = $1
  AND ownership_status = 'resolved'
  AND provider_customer_id IS NOT NULL
  AND btrim(provider_customer_id) <> ''
ORDER BY ingested_at DESC
LIMIT 1
`;

const OWNED_PAYMENT_CUSTOMER_SQL = `
SELECT provider_customer_id
FROM billing.billing_payments
WHERE subject_kind = 'user'
  AND subject_id = $1
  AND ownership_status = 'resolved'
  AND provider_customer_id IS NOT NULL
  AND btrim(provider_customer_id) <> ''
ORDER BY ingested_at DESC
LIMIT 1
`;

function checkoutUrlFromRaw(raw: unknown): string | null {
	return billingHalLinkHref(raw, "checkout");
}

async function ownedProviderCustomerId(input: {
	principal: AthenaPrincipal;
	sql: BillingSqlExecutor;
}): Promise<string | undefined> {
	const fromSubscription = await input.sql.query(
		OWNED_SUBSCRIPTION_CUSTOMER_SQL,
		[input.principal.userId],
	);
	const subscriptionCustomer = fromSubscription.rows[0]?.provider_customer_id;
	if (typeof subscriptionCustomer === "string" && subscriptionCustomer.trim()) {
		return subscriptionCustomer;
	}
	const fromPayment = await input.sql.query(OWNED_PAYMENT_CUSTOMER_SQL, [
		input.principal.userId,
	]);
	const paymentCustomer = fromPayment.rows[0]?.provider_customer_id;
	if (typeof paymentCustomer === "string" && paymentCustomer.trim()) {
		return paymentCustomer;
	}
	return undefined;
}

export async function createSelfCheckout(input: {
	allowedRedirectOrigins?: readonly string[];
	cancelUrl?: string;
	configuredProviders?: BillingProviderConfigMap;
	idempotencyKey: string;
	principal: AthenaPrincipal;
	priceId: string;
	registry?: BillingProviderRegistry;
	sql: BillingSqlExecutor;
	successUrl?: string;
	testMode?: boolean;
}): Promise<BillingCheckout> {
	const command = prepareBillingCommand({
		operation: "self.checkout.create",
		payload: {
			cancelUrl: input.cancelUrl,
			idempotencyKey: input.idempotencyKey,
			priceId: input.priceId,
			successUrl: input.successUrl,
		},
	});
	const priceId = String(command.payload.priceId);
	const idempotencyKey = String(command.payload.idempotencyKey);
	if (input.registry == null) {
		throw new AthenaBillingCapabilityError({
			operation: "self.checkout.create",
			reason: "runtime_unavailable",
		});
	}
	const delegated = selfDelegatedBillingPrincipal(input.principal, [
		"prices.list",
		"payments.create",
	]);
	const prices = await executeLocalBillingPriceList({
		authority: PROCESS_BILLING_INVOCATION,
		configuredProviders: input.configuredProviders,
		payload: {},
		principal: delegated,
		registry: input.registry,
		testMode: input.testMode,
	});
	const price: BillingPrice | undefined = prices.items.find(
		(item) => item.id === priceId,
	);
	if (price == null) {
		throw new AthenaBillingCapabilityError({
			operation: "self.checkout.create",
			reason: "missing_catalog",
		});
	}
	const intent = resolveBillingCheckoutIntent(price);
	if (intent.kind === "recurring") {
		throw new AthenaBillingCapabilityError({
			message:
				"Recurring catalog prices require recurring enrollment and must not execute as one-off payments.",
			operation: "self.checkout.create",
			reason: "unsupported_operation",
		});
	}
	const allowedRedirectOrigins = input.allowedRedirectOrigins ?? [];
	const successUrl = resolveAllowedBillingRedirectUrl({
		allowedOrigins: allowedRedirectOrigins,
		field: "successUrl",
		url: input.successUrl,
	});
	const cancelUrl = resolveAllowedBillingRedirectUrl({
		allowedOrigins: allowedRedirectOrigins,
		field: "cancelUrl",
		url: input.cancelUrl,
	});
	const redirectUrl = successUrl ?? cancelUrl;
	if (redirectUrl == null) {
		throw new AthenaBillingCapabilityError({
			operation: "self.checkout.create",
			reason: "unsupported_operation",
		});
	}
	const customerId = await ownedProviderCustomerId({
		principal: input.principal,
		sql: input.sql,
	});
	const payment = await executeLocalBillingPaymentCreate({
		authority: PROCESS_BILLING_INVOCATION,
		configuredProviders: input.configuredProviders,
		payload: {
			amount: price.amount,
			customerId,
			description: `Athena checkout ${price.id}`,
			idempotencyKey,
			metadata: {
				athenaSubjectId: input.principal.userId,
				athenaSubjectKind: "user",
				priceId: price.id,
			},
			redirectUrl,
		},
		principal: delegated,
		registry: input.registry,
		testMode: input.testMode,
	});
	return {
		checkoutUrl: checkoutUrlFromRaw(payment.raw),
		metadata: {
			paymentId: payment.providerPaymentId,
			priceId: price.id,
		},
		provider: payment.provider,
		providerCheckoutId: payment.providerPaymentId,
		raw: payment.raw,
	};
}
